/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/platform/design-system/components/ui/LoadingState.tsx
 * layer: web
 * area: components
 * kind: component
 * web_root: components
 * relpath: web/src/platform/design-system/components/ui/LoadingState.tsx
 * filename: LoadingState.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

import { clsx } from 'clsx';
import { Skeleton } from './Skeleton';

interface LoadingStateProps {
  variant?:
    | 'spinner'
    | 'skeleton-card'
    | 'skeleton-table'
    | 'skeleton-list'
    | 'skeleton-detail'
    | 'skeleton-form'
    | 'skeleton-dashboard';
  rows?: number;
  className?: string;
  label?: string;
  description?: string;
}

function Spinner({ className }: { className?: string }) {
  return (
    <div className={clsx('flex items-center justify-center py-12', className)}>
      <div className="h-8 w-8 motion-loading-spin rounded-full border-2 border-[rgb(var(--color-border))] border-t-[rgb(var(--color-text-primary))]" />
    </div>
  );
}

function SkeletonTable({ rows }: { rows: number }) {
  return (
    <div className="w-full space-y-3">
      <div className="flex gap-4">
        <Skeleton className="h-8 w-1/4" />
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-8 w-1/4" />
        <Skeleton className="h-8 w-1/6" />
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex gap-4">
          <Skeleton className="h-10 w-1/4" />
          <Skeleton className="h-10 w-1/3" />
          <Skeleton className="h-10 w-1/4" />
          <Skeleton className="h-10 w-1/6" />
        </div>
      ))}
    </div>
  );
}

function SkeletonCard({ rows }: { rows: number }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-bg-secondary))] p-4 space-y-3"
        >
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-4/5" />
        </div>
      ))}
    </div>
  );
}

function SkeletonList({ rows }: { rows: number }) {
  return (
    <div className="w-full space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-3 w-2/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

function SkeletonDetail() {
  return (
    <div className="w-full space-y-6">
      {/* Header with back + title + actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Skeleton className="h-8 w-8 rounded" />
          <div className="space-y-1">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-9 w-24 rounded-md" />
          <Skeleton className="h-9 w-24 rounded-md" />
        </div>
      </div>
      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="rounded-xl border border-[rgb(var(--color-border))] p-4 space-y-2"
          >
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-6 w-20" />
          </div>
        ))}
      </div>
      {/* Tabs + content */}
      <div className="space-y-4">
        <div className="flex gap-4 border-b border-[rgb(var(--color-border))]">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-9 w-20" />
          ))}
        </div>
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-4 w-full" style={{ maxWidth: `${85 - i * 8}%` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

function SkeletonForm() {
  return (
    <div className="w-full max-w-2xl space-y-6">
      {/* Title */}
      <Skeleton className="h-7 w-40" />
      {/* Fields */}
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>
      ))}
      {/* Two-column row */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>
      </div>
      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t border-[rgb(var(--color-border))]">
        <Skeleton className="h-10 w-24 rounded-md" />
        <Skeleton className="h-10 w-24 rounded-md" />
      </div>
    </div>
  );
}

function SkeletonDashboard() {
  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Skeleton className="h-7 w-40" />
        <div className="flex gap-2">
          <Skeleton className="h-9 w-28 rounded-md" />
          <Skeleton className="h-9 w-28 rounded-md" />
        </div>
      </div>
      {/* KPI row */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-bg-secondary))] p-4 space-y-2"
          >
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-3 w-24" />
          </div>
        ))}
      </div>
      {/* Chart area */}
      <div className="rounded-xl border border-[rgb(var(--color-border))] p-4">
        <Skeleton className="h-5 w-32 mb-4" />
        <Skeleton className="h-[200px] w-full rounded-md" />
      </div>
      {/* Two-column */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <div
            key={i}
            className="rounded-xl border border-[rgb(var(--color-border))] p-4 space-y-3"
          >
            <Skeleton className="h-5 w-28" />
            {Array.from({ length: 4 }).map((_, j) => (
              <div key={j} className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-1/3" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function LoadingState({
  variant = 'spinner',
  rows = 5,
  className,
  label = 'Loading',
  description,
}: LoadingStateProps) {
  return (
    <div
      className={clsx('w-full', className)}
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={label}
      data-state-kind="loading"
    >
      <span className="sr-only">{description ? `${label}. ${description}` : label}</span>
      {variant === 'spinner' && <Spinner />}
      {variant === 'skeleton-table' && <SkeletonTable rows={rows} />}
      {variant === 'skeleton-card' && <SkeletonCard rows={rows} />}
      {variant === 'skeleton-list' && <SkeletonList rows={rows} />}
      {variant === 'skeleton-detail' && <SkeletonDetail />}
      {variant === 'skeleton-form' && <SkeletonForm />}
      {variant === 'skeleton-dashboard' && <SkeletonDashboard />}
    </div>
  );
}
